import { countries } from "countries-list";

export const DEFAULT_ITEM_WEIGHT_GRAMS = 500;

const COUNTRY_CODE_MAP = {
  australia: "AU",
  "united states": "US",
  usa: "US",
  canada: "CA",
  china: "CN",
  germany: "DE",
  france: "FR",
  japan: "JP",
  singapore: "SG",
  "united kingdom": "GB",
  uk: "GB",
  curacao: "CW",
};

/**
 * 标准化国家名称用于模糊匹配，兼容 Curaçao / Curacao 等重音字符差异。
 * @param {string} value 国家名称
 * @returns {string} 标准化后的国家名称
 */
const normalizeCountryName = (value) => {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
};

/**
 * 根据 countries-list 构建国家名称到 ISO 两位国家码的映射。
 * @returns {Object} 国家名称映射表
 */
const buildCountryNameMap = () => {
  return Object.entries(countries).reduce((countryMap, [countryCode, country]) => {
    const countryName = normalizeCountryName(country?.name);
    if (countryName) {
      countryMap[countryName] = countryCode;
    }

    return countryMap;
  }, {});
};

const COUNTRY_NAME_MAP = buildCountryNameMap();

/**
 * 输出运费预估诊断日志，便于定位 4PX 预估和兜底运费的切换原因。
 * @param {string} scope 调用页面或业务范围
 * @param {string} message 日志说明
 * @param {Object} details 诊断详情
 */
export const logShippingEstimateDebug = (scope, message, details = {}) => {
  console.log(`[${scope}][ShippingEstimate] ${message}`, details);
};

/**
 * 输出国家码标准化诊断日志，方便确认是否会继续请求 4PX。
 * @param {string} scope 调用页面或业务范围
 * @param {string} country 原始国家
 * @param {string} countryCode 标准化后的国家码
 * @param {Object} details 诊断详情
 */
export const logCountryCodeDebug = (
  scope,
  country,
  countryCode,
  details = {}
) => {
  logShippingEstimateDebug(scope, "国家码解析结果", {
    rawCountry: country,
    countryCode,
    canRequest4px: Boolean(countryCode),
    ...details,
  });
};

/**
 * 将国家信息标准化为 ISO 两位国家码。
 * @param {string} country 收货国家（名称或国家码）
 * @returns {string} ISO 两位国家码，无法识别时返回空字符串
 */
export const normalizeCountryCode = (country) => {
  const rawValue = String(country || "").trim();
  if (!rawValue) {
    return "";
  }

  if (/^[A-Za-z]{2}$/.test(rawValue)) {
    return rawValue.toUpperCase();
  }

  const normalizedName = normalizeCountryName(rawValue);
  const mappedCode = COUNTRY_CODE_MAP[normalizedName] || COUNTRY_NAME_MAP[normalizedName];
  return mappedCode || "";
};

/**
 * 解析商品重量并转换为克（g）。
 * @param {number|string} weight 商品重量
 * @returns {number|null} 克数，无法解析时返回 null
 */
export const parseWeightToGrams = (weight) => {
  if (weight === null || weight === undefined) {
    return null;
  }

  const weightText = String(weight).trim().toLowerCase();
  if (!weightText) {
    return null;
  }

  const parsedWeight = Number.parseFloat(weightText.replace(/[^0-9.]/g, ""));
  if (!Number.isFinite(parsedWeight) || parsedWeight <= 0) {
    return null;
  }

  if (weightText.includes("kg")) {
    return parsedWeight * 1000;
  }

  return parsedWeight;
};

/**
 * 计算购物车总重量（克）。
 * @param {Array} items 购物车商品列表
 * @returns {number} 总重量（克）
 */
export const getCartTotalWeightInGrams = (items = []) => {
  return items.reduce((totalWeight, item) => {
    const itemWeight = parseWeightToGrams(item?.weight ?? item?.Weight);
    // TODO: 当前购物车未统一存储重量字段，暂用 500g 作为兜底值，后续可改为真实商品重量。
    const normalizedWeight = Number.isFinite(itemWeight)
      ? itemWeight
      : DEFAULT_ITEM_WEIGHT_GRAMS;
    const quantity = Number(item?.quantity || 0);
    return totalWeight + normalizedWeight * quantity;
  }, 0);
};

/**
 * 构建购物车运费预估诊断快照。
 * @param {Array} items 购物车商品列表
 * @returns {Array} 商品数量、重量和金额摘要
 */
export const buildCartShippingDebugItems = (items = []) => {
  return items.map((item) => {
    const rawWeight = item?.weight ?? item?.Weight ?? null;
    const parsedWeight = parseWeightToGrams(rawWeight);
    const quantity = Number(item?.quantity || 0);
    const unitWeight = Number.isFinite(parsedWeight)
      ? parsedWeight
      : DEFAULT_ITEM_WEIGHT_GRAMS;

    return {
      name: item?.name,
      quantity,
      price: Number(item?.price || 0),
      rawWeight,
      parsedWeight,
      usedDefaultWeight: !Number.isFinite(parsedWeight),
      unitWeightInGrams: unitWeight,
      lineWeightInGrams: unitWeight * quantity,
      lineAmount: Number((Number(item?.price || 0) * quantity).toFixed(2)),
    };
  });
};

/**
 * 构建 4PX 运费预估请求参数。
 * @param {Array} cartItems 购物车商品列表
 * @param {string} country 收货国家（名称或国家码）
 * @returns {{payload: Object|null, countryCode: string, totalWeightInGrams: number}}
 */
export const buildShippingEstimatePayload = (cartItems = [], country) => {
  const countryCode = normalizeCountryCode(country);
  const debugItems = buildCartShippingDebugItems(cartItems);
  const totalWeightInGrams = Math.max(
    1,
    Math.round(
      debugItems.reduce((totalWeight, item) => {
        return totalWeight + Number(item?.lineWeightInGrams || 0);
      }, 0)
    )
  );

  if (!countryCode) {
    return {
      payload: null,
      countryCode,
      totalWeightInGrams,
    };
  }

  return {
    payload: {
      country_code: countryCode,
      weight: String(totalWeightInGrams),
      cargocode: "P",
    },
    countryCode,
    totalWeightInGrams,
    debugItems,
  };
};

/**
 * 获取 4PX 报价中的物流产品代码。
 * @param {Object} quote 4PX 报价项
 * @returns {string} 物流产品代码
 */
export const getShippingQuoteCode = (quote = {}) => {
  return String(
    quote?.logisticsProductCode ||
      quote?.logistics_product_code ||
      quote?.productCode ||
      quote?.product_code ||
      ""
  ).trim();
};

/**
 * 获取 4PX 报价中的运费金额。
 * @param {Object} quote 4PX 报价项
 * @returns {number|null} 运费金额
 */
export const getShippingQuoteFee = (quote = {}) => {
  const rawFee =
    quote?.lumpSumFee ??
    quote?.lump_sum_fee ??
    quote?.totalFee ??
    quote?.total_fee ??
    quote?.estimatedCost ??
    quote?.estimated_cost ??
    quote?.fee ??
    quote?.cost;
  const parsedFee = Number(rawFee);
  return Number.isFinite(parsedFee) && parsedFee >= 0
    ? Number(parsedFee.toFixed(2))
    : null;
};

/**
 * 获取 4PX 报价中的渠道名称。
 * @param {Object} quote 4PX 报价项
 * @returns {string} 渠道名称
 */
export const getShippingQuoteName = (quote = {}) => {
  return String(
    quote?.logisticsProductName ||
      quote?.logistics_product_name ||
      quote?.logistics_product_name_en ||
      quote?.logistics_product_name_cn ||
      quote?.remarks ||
      ""
  ).trim();
};

/**
 * 从后端预估响应中提取所有可选择的 4PX 渠道。
 * @param {Object} response 后端 `/logistics/estimate` 响应
 * @param {string} scope 调用页面或业务范围
 * @returns {Array<Object>} 可选择的物流渠道列表
 */
export const extractShippingQuoteOptions = (response, scope = "Checkout") => {
  const estimateWrapper = response?.estimate || {};
  const quoteSources = [
    estimateWrapper?.quotes,
    estimateWrapper?.raw?.costList,
    estimateWrapper?.raw?.data,
    estimateWrapper?.raw,
  ];
  const quoteList = quoteSources.find(Array.isArray) || [];

  const options = quoteList
    .map((quote, index) => {
      const fee = getShippingQuoteFee(quote);
      const logisticsProductCode = getShippingQuoteCode(quote);
      if (!Number.isFinite(fee) || !logisticsProductCode) {
        return null;
      }

      const logisticsProductName = getShippingQuoteName(quote);
      const estimatedTime = quote?.estimatedTime || quote?.estimated_time || "";
      const chargeWeight = quote?.chargeWeight || quote?.charge_weight || "";

      return {
        id: `${logisticsProductCode}-${index}`,
        logisticsProductCode,
        logisticsProductName,
        fee,
        currency: quote?.currency || quote?.currencyCode || "USD",
        chargeWeight,
        estimatedTime,
        isShowTrack: quote?.isShowTrack || quote?.is_show_track || "",
        logisticsChannelNo: quote?.logisticsChannelNo || quote?.logistics_channel_no || "",
        remarks: quote?.remarks || quote?.remark || "",
        rawQuote: quote,
      };
    })
    .filter(Boolean);

  logShippingEstimateDebug(scope, "解析 4PX 可选物流渠道", {
    optionCount: options.length,
    options,
  });

  return options;
};

/**
 * 格式化物流渠道下拉选项文案。
 * @param {Object} option 物流渠道选项
 * @returns {string} 下拉显示文案
 */
export const formatShippingQuoteOptionLabel = (option = {}) => {
  const code = option.logisticsProductCode || "4PX";
  const name = option.logisticsProductName ? ` - ${option.logisticsProductName}` : "";
  const time = option.estimatedTime ? ` / ${option.estimatedTime} days` : "";
  return `${code}${name} - $${Number(option.fee || 0).toFixed(2)}${time}`;
};

/**
 * 格式化物流渠道收起状态文案，只显示价格。
 * @param {Object} option 物流渠道选项
 * @returns {string} 下拉收起时显示的价格
 */
export const formatShippingQuoteSelectedLabel = (option = {}) => {
  return `$${Number(option.fee || 0).toFixed(2)}`;
};

/**
 * 从后端预估响应中提取可用运费。
 * @param {Object} response 后端 `/logistics/estimate` 响应
 * @param {string} scope 调用页面或业务范围
 * @returns {number|null} 运费金额，无法提取时返回 null
 */
export const extractEstimatedShippingFee = (response, scope = "Checkout") => {
  const estimateWrapper = response?.estimate || {};
  const quoteList = Array.isArray(estimateWrapper?.quotes)
    ? estimateWrapper.quotes
    : [];
  const rawCostList = Array.isArray(estimateWrapper?.raw?.costList)
    ? estimateWrapper.raw.costList
    : [];

  const candidates = [
    estimateWrapper?.estimate,
    quoteList[0],
    rawCostList[0],
  ].filter(Boolean);

  logShippingEstimateDebug(scope, "开始解析 4PX 运费响应", {
    quoteCount: quoteList.length,
    rawCostCount: rawCostList.length,
    candidateCount: candidates.length,
    firstEstimate: estimateWrapper?.estimate || null,
    rawResponse: response,
  });

  for (const candidate of candidates) {
    const parsedFee = getShippingQuoteFee(candidate);
    logShippingEstimateDebug(scope, "检查 4PX 运费候选字段", {
      candidate,
      parsedFee,
    });
    if (Number.isFinite(parsedFee) && parsedFee >= 0) {
      return parsedFee;
    }
  }

  return null;
};
